/**
 * Access control for AimerPay - Adyen Balance Platform
 *
 * Role Hierarchy:
 * - SuperAdmin: Platform management + can enter any MerchantOwner context
 * - MerchantOwner: Full access to all features
 * - MerchantAccountant: View transactions, reports, process refunds
 * - MerchantViewer: View transactions only
 */

import { getActingAsMerchantOwner } from '@/models/actingAsMerchant';
import { getCurrentMerchantRole } from '@/models/merchant';

// Role constants
export const ROLES = {
  SUPER_ADMIN: 'SuperAdmin',
  ADMIN: 'Admin',
  MERCHANT_OWNER: 'MerchantOwner',
  MERCHANT_ACCOUNTANT: 'MerchantAccountant',
  MERCHANT_VIEWER: 'MerchantViewer',
  // Per-partner grant roles (UserPartner.myRole only — never a global user role):
  // another owner granted this user access to specific partners
  PARTNER_OWNER: 'PartnerOwner',
  PARTNER_VIEWER: 'PartnerViewer',
} as const;

/**
 * Read-only on a given merchant: a cross-owner PartnerViewer grant, or the
 * legacy per-partner MerchantViewer row. The backend 403s every non-GET request
 * for these roles (ValidatePartnerAccessAsync, Phase 32); the UI mirrors that.
 * An unknown role means "not restricted" — see getCurrentMerchantRole.
 */
export const isReadOnlyMerchantRole = (role?: string) =>
  role === ROLES.PARTNER_VIEWER || role === ROLES.MERCHANT_VIEWER;

// Helper to check if user has merchants using user-specific localStorage keys
const checkHasMerchants = (userId?: string) => {
  if (!userId) return false;
  const merchantsKey = `aimerpay_merchants_${userId}`;
  const merchantsJson = localStorage.getItem(merchantsKey);
  if (merchantsJson) {
    try {
      const merchants = JSON.parse(merchantsJson);
      return Array.isArray(merchants) && merchants.length > 0;
    } catch {
      return false;
    }
  }
  return false;
};

export default function access(
  initialState: { currentUser?: API.CurrentUser } | undefined,
) {
  const { currentUser } = initialState ?? {};
  const role = currentUser?.role || currentUser?.access;
  const userId = currentUser?.id || currentUser?.userid;

  // Check specific roles
  const isSuperAdmin = role === ROLES.SUPER_ADMIN;
  const isAdmin = role === ROLES.ADMIN;
  const isOwner = role === ROLES.MERCHANT_OWNER;
  const isAccountant = role === ROLES.MERCHANT_ACCOUNTANT;
  const isViewer = role === ROLES.MERCHANT_VIEWER;

  // Check if user has merchants (using user-specific key)
  const hasMerchants = checkHasMerchants(userId);

  // Acting-as-owner mode, read from sessionStorage. SuperAdmin can enter any
  // MerchantOwner; Admin enters an owner via a SuperAdmin-approved time-boxed
  // grant (same enterMerchantOwnerMode flow — the backend enforces the grant).
  // Once entered, the sidebar + edit gates behave identically for both roles.
  const actingAsMerchantOwner =
    isSuperAdmin || isAdmin ? getActingAsMerchantOwner() : null;
  const inMerchantMode = !!actingAsMerchantOwner;

  // The global role is only half the story since Phase 32: a global
  // MerchantOwner can hold a read-only grant on the merchant they just entered.
  // Menu visibility follows the EFFECTIVE role on the current merchant, so a
  // read-only merchant collapses the sidebar to Dashboard + Transactions,
  // exactly like a global MerchantViewer.
  const isReadOnlyMerchant = isReadOnlyMerchantRole(
    getCurrentMerchantRole(userId),
  );

  return {
    // Basic access - any logged in user
    canAccess: !!currentUser,

    // SuperAdmin access
    isSuperAdmin,
    canViewSplitConfigurations: isSuperAdmin,
    canInviteAdmin: isSuperAdmin,
    // Which Adyen merchant account a partner is onboarded onto — and therefore its
    // default commission split configuration. Same authority as split configs.
    // NOTE: must NOT be derived from canCreatePartner, which is also true for a plain
    // Admin acting-as an owner; GET /api/admin/adyen-profiles is SuperAdmin-only and
    // would 403 for them. Stays true in merchant mode, matching the backend's
    // User.IsInRole("SuperAdmin") check on POST/PUT /api/partners.
    canSelectAdyenProfile: isSuperAdmin,

    // Admin panel - SuperAdmin only, hidden in merchant mode
    canViewAdmin: isSuperAdmin && !inMerchantMode,

    // Plain Admin (platform staff, Role=1) — distinct from SuperAdmin
    isAdmin,
    // Admin browses all partners to request time-boxed access (Request/Enter workflow)
    canRequestPartnerAccess: isAdmin,
    // SuperAdmin reviews/approves/rejects/revokes Admin access requests
    canApproveAccessRequests: isSuperAdmin,

    // Whether user has merchants
    hasMerchants: inMerchantMode || hasMerchants,

    // Role checks
    isOwner,
    isAccountant,
    isViewer,

    // Acting as merchant owner (for banner and menu filtering)
    actingAsMerchantOwner,
    inMerchantMode,

    // Read-only role on the merchant currently entered (PartnerViewer grant or
    // legacy per-partner MerchantViewer). Drives the menu collapse below.
    isReadOnlyMerchant,

    // Can manage (create/edit/delete) the currently entered partner's business data.
    // SuperAdmin always; Admin only after entering a partner via time-boxed grant.
    // MerchantOwner remains read-only in detail views (unchanged from before).
    canManageCurrentPartner:
      (isSuperAdmin || inMerchantMode) && !isReadOnlyMerchant,

    // SuperAdmin can view all partners (merchant list)
    canViewAllPartners: isSuperAdmin,

    // Dashboard - SuperAdmin in merchant mode also sees dashboard
    canViewDashboard: (!!currentUser && !isSuperAdmin) || inMerchantMode,

    // Partners/Merchants - Owner can always access; SuperAdmin only in merchant mode;
    // Admin browses the full list to request time-boxed access (Request/Enter workflow)
    canViewPartners:
      (isOwner || inMerchantMode || isAdmin) && !isReadOnlyMerchant,
    canManagePartners: (isOwner || inMerchantMode) && !isReadOnlyMerchant,

    // Creating a merchant is platform staff only: SuperAdmin, or Admin/SuperAdmin
    // acting as an owner (inMerchantMode). A MerchantOwner manages and edits the
    // merchants provisioned for them, but no longer creates their own — merchant
    // details feed the Adyen legal entity and are hard to correct after onboarding.
    canCreatePartner: (isSuperAdmin || inMerchantMode) && !isReadOnlyMerchant,

    // Merchant list (read-only landing page) - anyone who can have merchants,
    // including Accountant/Viewer, who lack canViewPartners (no CRUD rights).
    // Gates the /merchants route so all roles can land-then-enter a merchant
    // workspace; canManagePartners still controls the CRUD row actions.
    canViewMerchantList:
      isOwner || inMerchantMode || isAdmin || isAccountant || isViewer,

    // Legacy aliases
    canViewAccountHolders: isOwner || inMerchantMode,
    canManageAccountHolders: isOwner || inMerchantMode,

    // Balance Accounts - Owner only WITH merchants, or SuperAdmin in merchant mode
    canViewBalanceAccounts:
      ((isOwner && hasMerchants) || inMerchantMode) && !isReadOnlyMerchant,
    canManageBalanceAccounts:
      ((isOwner && hasMerchants) || inMerchantMode) && !isReadOnlyMerchant,
    canCreateBalanceAccount:
      ((isOwner && hasMerchants) || inMerchantMode) && !isReadOnlyMerchant,

    // Transactions - SuperAdmin in merchant mode also gets access
    canViewTransactions:
      (!!currentUser && hasMerchants && !isSuperAdmin) || inMerchantMode,

    // Payouts - SuperAdmin in merchant mode also gets access
    canViewPayouts:
      (((isOwner || isAccountant) && hasMerchants) || inMerchantMode) &&
      !isReadOnlyMerchant,

    // Reports - SuperAdmin in merchant mode also gets access
    canViewReports:
      (((isOwner || isAccountant) && hasMerchants) || inMerchantMode) &&
      !isReadOnlyMerchant,

    // Disputes - SuperAdmin in merchant mode also gets access
    canViewDisputes:
      (((isOwner || isAccountant) && hasMerchants) || inMerchantMode) &&
      !isReadOnlyMerchant,

    // Staff Management - Owner only (NOT for SuperAdmin in merchant mode).
    // Deliberately NOT gated on isReadOnlyMerchant: this is an owner-level
    // (per-person) capability under /account, independent of which merchant is
    // entered — a user may own merchant A while holding a viewer grant on B.
    canInviteStaff: isOwner,
    canManageStaff: isOwner,

    // Partner Grants - owner grants own partners to other users (PartnerOwner/
    // PartnerViewer). SuperAdmin/Admin acting-as an owner included; the backend
    // validates real ownership per partner either way. Owner-level like the
    // staff flags above, so not gated on isReadOnlyMerchant either — the page
    // already filters its list down to partners the user actually owns.
    canGrantPartners: isOwner || inMerchantMode,

    // Settings - all users except SuperAdmin in merchant mode
    canViewSettings: !!currentUser && !inMerchantMode,
  };
}
